import { call, put, select, takeEvery } from 'redux-saga/effects'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { HomeConstants } from '../../home/store/home.constants'
import { getPcrIdSelector } from '../../home/selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getUpdatedCustomElementType, populateCustomElementsFromPCR } from '../utils/customElements.util'
import { setCustomDisplayData } from '../store/nemsisCustomData.actions'
import { getNemsisCustomDataOptionsSelector } from '../../options/selectors'
import { INemsisCustomDataOptions } from '../../options/store/options.types'
import { randomNumber } from '../../../utilities'
import { IConfigurationsState, IDataset } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { ConfigurationsConstants } from '../../configurations/store/configurations.constants'
import { IStateCustomElements } from '../store/nemsisCustomData.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setHideNemsisCustomOption } from '../../options/store/options.actions'

const allGroups = [
  'eVitals.VitalGroup',
  'eAirway.ConfirmationGroup',
  'eProcedures.ProcedureGroup',
  'eMedications.MedicationGroup',
]

const isUSNationData = (state: string): boolean => {
  return /^(US|US[-_\s][A-Za-z0-9.]+)$/i.test(state)
}

export function* setStateCustomDisplayData() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const nemsisCustomDataOptions: INemsisCustomDataOptions = yield select(getNemsisCustomDataOptionsSelector)
  const { datasetDeterminationConfig }: IConfigurationsState = yield select(getConfigurationsSelector)

  if (!pcrID || !datasetDeterminationConfig?.datasets || !nemsisCustomDataOptions.customDataWithState) {
    return
  }
  try {
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    const datasetCodes = datasetDeterminationConfig.datasets.map((item: IDataset) => item.dataset)
    /* Filter Supported states */
    const savedStatesWithData = pcrDoc.customData?.map((stateElement) => stateElement.state)

    const unsupportedDatasets = new Set(
      datasetDeterminationConfig?.datasets
        ?.filter(({ isStateSupportedInNOW }) => isStateSupportedInNOW === 0)
        .map(({ dataset }) => dataset)
    )
    const hasNationalCustomExport = nemsisCustomDataOptions.customDataWithState.some(
      ({ state, exportNationalCustom }) =>
        ((datasetCodes.includes(state) && !unsupportedDatasets?.has(state)) || savedStatesWithData?.includes(state)) &&
        exportNationalCustom === 1
    )

    const newStateCustomElements = nemsisCustomDataOptions.customDataWithState
      .filter(({ state }) => {
        const stateDataIsSaved = savedStatesWithData?.includes(state)
        const isStateInDatasets = datasetCodes.includes(state) && !unsupportedDatasets?.has(state)
        return isStateInDatasets || (isUSNationData(state) && hasNationalCustomExport) || stateDataIsSaved
      })
      .map((stateElement) => {
        const { datasetDetermination, defaultDataset, datasets } = datasetDeterminationConfig

        const isDefaultDataState =
          datasetDetermination === 1
            ? defaultDataset === stateElement.state
            : datasets?.some(({ dataset, includeInExport }) => dataset === stateElement.state && includeInExport > 0) ??
              false

        return { ...stateElement, isDefaultDataState }
      })
      .sort((a, b) => Number(b.isDefaultDataState) - Number(a.isDefaultDataState))

    const updatedStateCustomElements = newStateCustomElements
      .map((stateElement) => {
        const filteredGroups = stateElement.groups
          .map((group) => {
            const matchingGroup = pcrDoc.customDisplayData
              ?.find((stateData) => stateData.state === stateElement.state)
              ?.groups.find((g) => g.groupId === group.groupId)

            const filteredCustomElements = group.customElements.filter(
              (customElement) =>
                !(
                  (!customElement?.mergedoptions?.length &&
                    !customElement?.recurrence &&
                    customElement?.dataType === 'Other') ||
                  customElement.report_column_table ||
                  customElement.report_column_column ||
                  customElement.report_column_codetable ||
                  customElement.codetable
                )
            )

            if (filteredCustomElements.length === 0) return null

            return {
              ...group,
              groupKey: matchingGroup?.groupKey ?? randomNumber().toString(),
              customElements: filteredCustomElements.map((customElement) => {
                const matchingCustomElement = matchingGroup?.customElements.find((ce) => ce.id === customElement.id)

                return {
                  ...customElement,
                  key: matchingCustomElement?.key ?? randomNumber().toString(),
                  customElementSequence: 1,
                  customElementType: getUpdatedCustomElementType(customElement),
                }
              }),
            }
          })
          .filter(Boolean)

        if (filteredGroups.length === 0) return null

        return {
          ...stateElement,
          groups: filteredGroups,
        }
      })
      .filter(Boolean)

    const newStateCustomElementsPopulated = populateCustomElementsFromPCR(
      updatedStateCustomElements as IStateCustomElements,
      pcrDoc.customDisplayData
    )
    const filteredCustomData = newStateCustomElementsPopulated.map((stateData) => ({
      ...stateData,
      groups: stateData.groups.filter(
        (group) => group?.procedureKey || group.groupId === null || !allGroups.includes(group.groupId)
      ),
    }))
    yield put(setHideNemsisCustomOption(!filteredCustomData?.length))
    yield put(setCustomDisplayData(filteredCustomData))
  } catch (e) {
    yield put(throwPcrException(`setStateCustomDisplayDataSaga() error: ${e}`))
  }
}

export function* setStateCustomDisplayDataSaga() {
  yield takeEvery(
    [HomeConstants.ON_SELECT_PCR, HomeConstants.SET_NEW_PCR, ConfigurationsConstants.SET_DATASET_DETERMINATION_CONFIG],
    performWithMutexHOG,
    setStateCustomDisplayData
  )
}
